/* @flow */
import { combineReducers } from 'redux-immutable';
import { OrderedMap, Map } from 'immutable';
import type { Action } from 'trustar/utils/redux-types.js';
import { pickImmutable } from 'trustar/utils';
import { prop } from 'ramda';
import { REHYDRATE } from 'redux-persist-immutable/constants';

import {
  FETCH,
  FETCH_SUCCESS,
  FETCH_FAILURE,

  FETCH_WITH_QUERY,
  FETCH_WITH_QUERY_SUCCESS,
  FETCH_WITH_QUERY_FAILURE,
  SET_QUERY_RESULTS,

  SAVE,
  SAVE_SUCCESS,
  SAVE_FAILURE,

  DELETE,
  DELETE_SUCCESS,
  DELETE_FAILURE,

  SET_DISPLAY_OPTIONS,
} from './action-types.js';

import {
  SAVE_ENCLAVE_ICON_SUCCESS,
  SAVE_ENCLAVE_ICON_FAILURE,
} from 'modules/icons/action-types.js';

import { Options, type Enclave, type RawOptions } from 'modules/enclave/types.js';

type State = OrderedMap<string, Enclave>;

export const enclaves = (state: State = OrderedMap(), action: Action<*>) => {
  const { type, payload } = action;

  switch (type) {
  case FETCH_SUCCESS:
    return payload;
  case SAVE_SUCCESS:
    return state.set(payload.id, payload);
  case SAVE_ENCLAVE_ICON_SUCCESS:
    const fieldToSave = payload.iconUrl.includes('.svg') ? 'iconSvgUrl' : 'iconUrl';
    return state.setIn([payload.guid, fieldToSave], payload.iconUrl);
  case DELETE_SUCCESS:
    return state.delete(payload);
  default:
    return state;
  }
};
/**
 * Why have an additional store item for enclaves? Because enclaves is hooked to too many codependent objects so querying on it
 * tends have to have unexpected side effects. This is not the most elegant solution, but this will provide any enclave filtering
 * tools that require redux to get their search results here without it effecting the general enclave map
 * @param state
 * @param action
 * @returns {Map<K, V>|*|Map<*, V>|State|Map<*, *>}
 */
export const enclavesQueryResult = (state: State = OrderedMap(), action: Action<*>) => {
  const { type, payload } = action;

  switch (type) {
  case FETCH_SUCCESS:
  case FETCH_WITH_QUERY_SUCCESS:
  case SET_QUERY_RESULTS: // SET is used to manually set the result, such as when resetting
    return payload;
  case SAVE_SUCCESS:
    return state.set(payload.id, payload);
  case SAVE_ENCLAVE_ICON_SUCCESS:
    const fieldToSave = payload.iconUrl.includes('.svg') ? 'iconSvgUrl' : 'iconUrl';
    return state.setIn([payload.guid, fieldToSave], payload.iconUrl);
  case DELETE_SUCCESS:
    return state.delete(payload);
  default:
    return state;
  }
};

export const options = (state: typeof Options = Options(), action: Action<RawOptions>) => {
  const { type, payload } = action;

  switch (type) {
    case SET_DISPLAY_OPTIONS:
    return state.merge(payload);
  default:
    return state;
  }
};

/**
 * NOTE: Pre-existing state always takes precedence over new color state. This
 * is because we persist colors specifically for consistency so we favor the
 * existing values over new values.
 */
export const colors = (state: Map<string, string> = Map(), action: Action<*>) => {
  const { type, payload } = action;

  switch (type) {

  // NOTE: The reason we do this manually is that the rehydrate action
  // overwrites state. They recommend buffering actions so as to ensure the
  // rehydration happens first, but this is tough since som of our epics are
  // stateful
  case REHYDRATE:
    // NOTE: Yeah, payload is a very odd plain object of app state keys mapped
    // to the serialized app state... so we have a plain object with immutable
    // objects at each key
    try {
      return state.merge(payload.enclaves.get('colors'));
    } catch (err) {
      return state;
    }

  case FETCH_SUCCESS:
    return payload.map(prop('color')).merge(state);
  case SAVE_SUCCESS:
    return state.update(payload.id, c => c || payload.color);
  default:
    return state;
  }
};

export const loading = (state: boolean = true, action: Action<any>) => {
  switch (action.type) {
  case FETCH:
  case SAVE:
  case DELETE:
    return true;
  case FETCH_SUCCESS:
  case FETCH_FAILURE:
  case SAVE_SUCCESS:
  case SAVE_FAILURE:
  case DELETE_SUCCESS:
  case DELETE_FAILURE:
  case SAVE_ENCLAVE_ICON_SUCCESS:
  case SAVE_ENCLAVE_ICON_FAILURE:
    return false;
  default:
    return state;
  }
};
export const queriedEnclavesLoading = (state: boolean = true, action: Action<any>) => {
  switch (action.type) {
    case FETCH_WITH_QUERY:
      return true;
    case FETCH_WITH_QUERY_SUCCESS:
    case FETCH_WITH_QUERY_FAILURE:
      return false;
    default:
      return state;
  }
};

export const loaded = (state: boolean = false, action: Action<any>) => {
  switch(action.type) {
  case FETCH_SUCCESS:
  case FETCH_FAILURE:
    return true;
  default:
    return state;
  }
};

export const serialize = (state: Map<any, any>) =>
  pickImmutable(['colors'], state);

export default combineReducers({
  enclaves,
  loading,
  loaded,
  options,
  colors,
  queriedEnclavesLoading,
  enclavesQueryResult,
});



// WEBPACK FOOTER //
// ./src/app/modules/enclave-management/stores.js